import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Marquee from 'react-fast-marquee';
import {
  getPrefersReducedMotion,
  shouldAnimateMarquee,
  textOverflowsContainer,
} from './textMarqueeLogic';

interface TextMarqueeProps {
  text: string;
  /** Applied to the outer wrapper (keeps call-site layout classes). */
  className?: string;
  style?: React.CSSProperties;
  /** Optional click handler (e.g. open EGM); does not block pause-toggle. */
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Horizontal marquee for overflowing single-line text.
 * Short / fitting text stays static. Respects prefers-reduced-motion (static + ellipsis).
 * Only animates while in view (IntersectionObserver) for list performance.
 */
const TextMarquee: React.FC<TextMarqueeProps> = ({ text, className, style, onClick }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const isResettingRef = useRef(false);

  const [overflows, setOverflows] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(getPrefersReducedMotion);
  const [marqueeRun, setMarqueeRun] = useState(true);
  const [marqueePause, setMarqueePause] = useState(false);
  const [marqueeDelay, setMarqueeDelay] = useState(1);
  /** Remount key so react-fast-marquee restarts cleanly after remeasure. */
  const [cycleKey, setCycleKey] = useState(0);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const probe = measureRef.current;
    if (!wrap || !probe) return;
    const next = textOverflowsContainer(probe.scrollWidth, wrap.clientWidth);
    setOverflows(next);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [text, measure]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    const onResize = () => {
      isResettingRef.current = true;
      measure();
      setMarqueeRun(true);
      setMarqueePause(false);
      setMarqueeDelay(1);
      setCycleKey((k) => k + 1);
      window.setTimeout(() => {
        isResettingRef.current = false;
      }, 250);
    };
    window.addEventListener('orientationchange', onResize);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('orientationchange', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, [measure]);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const onChange = () => setReducedMotion(getPrefersReducedMotion());
    onChange();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0);
      },
      { root: null, threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // When text changes, allow a fresh cycle.
  useEffect(() => {
    setMarqueeRun(true);
    setMarqueePause(false);
    setMarqueeDelay(1);
    setCycleKey((k) => k + 1);
  }, [text]);

  const animate = shouldAnimateMarquee({
    overflows,
    isVisible,
    prefersReducedMotion: reducedMotion,
  });

  const handleClick = (e: React.MouseEvent) => {
    onClick?.(e);
    if (!animate) return;
    setMarqueeRun(true);
    setMarqueePause((prev) => !prev);
    setMarqueeDelay(0);
  };

  const onCompleteMarquee = () => {
    if (isResettingRef.current) return;
    setMarqueeRun(false);
    setMarqueePause(true);
  };

  if (!text) return null;

  return (
    <div
      ref={wrapRef}
      className={['text-marquee', className].filter(Boolean).join(' ')}
      style={style}
      onClick={handleClick}
    >
      {/* Hidden probe for width measurement (always full text, never animated). */}
      <span ref={measureRef} className="text-marquee__measure" aria-hidden="true">
        {text}
      </span>

      {animate ? (
        <Marquee
          key={cycleKey}
          play={marqueeRun && !marqueePause && isVisible}
          delay={marqueeDelay}
          gradient={false}
          speed={60}
          onCycleComplete={onCompleteMarquee}
        >
          <span className="text-marquee__text">
            {text}
            <span className="text-marquee__gap" aria-hidden="true">
              &nbsp;
            </span>
          </span>
        </Marquee>
      ) : (
        <span className={`text-marquee__text text-marquee__text--static${overflows ? ' text-marquee__text--ellipsis' : ''}`}>
          {text}
        </span>
      )}
    </div>
  );
};

export default TextMarquee;
export { textOverflowsContainer, shouldAnimateMarquee, getPrefersReducedMotion } from './textMarqueeLogic';
